import React, { Component } from "react";
import {
  Card,
  CardBody,
  Col,
  Row,
  Button,
  Container,
  Badge,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormGroup,
  Label,
  Input,
} from "reactstrap";
import {
  getTransportRequestById,
  getActiveTransportDrivers,
  acceptTransportRequest,
  rejectTransportRequest,
  assignTransportDriver,
  updateTransportRequestStatus,
  cancelTransportRequest,
  createTransportDriver,
  setTransportQuote,
  updateTransportPaymentStatus,
} from "../../Endpoint/Admin/index";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import { ToastContainer, toast } from "react-toastify";
import { Link } from "react-router-dom";
import ConfirmModal from "../../components/Common/ConfirmModal";

class TransportRequestDetail extends Component {
  constructor(props) {
    super(props);
    this.state = {
      request: null,
      drivers: [],
      isLoaded: false,
      showDriverModal: false,
      showNewDriverModal: false,
      showQuoteModal: false,
      selectedDriverId: "",
      newDriver: {
        name: "",
        mobile: "",
        vehicleType: "sedan",
        vehicleNumber: "",
      },
      quoteData: {
        amount: "",
        perKmRate: "",
        estimatedKm: "",
        breakdown: "",
      },
      isSubmittingQuote: false,
      showPaymentModal: false,
      isUpdatingPayment: false,
      // Confirmation modal states
      showAcceptModal: false,
      showReasonModal: false,
      showPaymentConfirmModal: false,
      reasonModalType: "", // "reject" or "cancel"
      reasonText: "",
      actionLoading: false,
      breadcrumbItems: [
        { title: "Dashboard", link: "/dashboard" },
        { title: "Transport Requests", link: "/transport/requests" },
        { title: "Request Details", link: "#" },
      ],
    };
  }

  componentDidMount() {
    this.loadRequest();
    this.loadDrivers();
  }

  loadRequest = () => {
    const { id } = this.props.match.params;
    getTransportRequestById(id)
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ request: result.data, isLoaded: true });
        } else {
          toast.error(result?.message || "Failed to load request");
        }
      })
      .catch(() => toast.error("Error loading request"));
  };

  loadDrivers = () => {
    getActiveTransportDrivers()
      .then((result) => {
        if (result && result.status === 200) {
          this.setState({ drivers: result.data });
        }
      })
      .catch(() => console.error("Error loading drivers"));
  };

  // Modal toggle handlers
  toggleAcceptModal = () => this.setState({ showAcceptModal: !this.state.showAcceptModal });
  toggleReasonModal = () => this.setState({ showReasonModal: !this.state.showReasonModal, reasonText: "" });
  togglePaymentConfirmModal = () => this.setState({ showPaymentConfirmModal: !this.state.showPaymentConfirmModal });

  handleAccept = () => {
    this.setState({ showAcceptModal: true });
  };

  confirmAccept = () => {
    const { request } = this.state;
    this.setState({ actionLoading: true });
    acceptTransportRequest(request._id)
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Request accepted");
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to accept");
        }
      })
      .finally(() => {
        this.setState({ actionLoading: false, showAcceptModal: false });
      });
  };

  handleReject = () => {
    this.setState({ reasonModalType: "reject", showReasonModal: true });
  };

  confirmReasonAction = () => {
    const { request, reasonModalType, reasonText } = this.state;
    if (!reasonText.trim()) {
      toast.error("Please enter a reason");
      return;
    }
    this.setState({ actionLoading: true });

    const apiCall = reasonModalType === "reject"
      ? rejectTransportRequest(request._id, reasonText)
      : cancelTransportRequest(request._id, reasonText);

    apiCall
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(reasonModalType === "reject" ? "Request rejected" : "Request cancelled");
          this.loadRequest();
        } else {
          toast.error(response?.message || `Failed to ${reasonModalType}`);
        }
      })
      .finally(() => {
        this.setState({
          actionLoading: false,
          showReasonModal: false,
          reasonText: "",
          reasonModalType: "",
        });
      });
  };

  handleSetQuote = () => {
    const { request, quoteData } = this.state;

    if (!quoteData.amount || parseFloat(quoteData.amount) <= 0) {
      toast.error("Please enter a valid quote amount");
      return;
    }

    this.setState({ isSubmittingQuote: true });

    setTransportQuote(request._id, {
      amount: parseFloat(quoteData.amount),
      perKmRate: quoteData.perKmRate ? parseFloat(quoteData.perKmRate) : null,
      estimatedKm: quoteData.estimatedKm ? parseFloat(quoteData.estimatedKm) : null,
      breakdown: quoteData.breakdown || null,
    })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Quote sent to customer. Awaiting response.");
          this.setState({
            showQuoteModal: false,
            quoteData: { amount: "", perKmRate: "", estimatedKm: "", breakdown: "" },
          });
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to set quote");
        }
      })
      .catch(() => toast.error("Error setting quote"))
      .finally(() => this.setState({ isSubmittingQuote: false }));
  };

  handleCancel = () => {
    this.setState({ reasonModalType: "cancel", showReasonModal: true });
  };

  handleMarkPaymentReceived = () => {
    this.setState({ showPaymentConfirmModal: true });
  };

  confirmMarkPaymentReceived = () => {
    const { request } = this.state;
    this.setState({ isUpdatingPayment: true, showPaymentConfirmModal: false });

    updateTransportPaymentStatus(request._id, {
      paymentStatus: "paid",
      notes: "Payment received and confirmed by admin",
    })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Payment marked as received");
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to update payment status");
        }
      })
      .catch(() => toast.error("Error updating payment status"))
      .finally(() => this.setState({ isUpdatingPayment: false }));
  };

  handleAssignDriver = () => {
    const { request, selectedDriverId, drivers } = this.state;

    if (!selectedDriverId) {
      toast.error("Please select a driver");
      return;
    }

    const driver = drivers.find((d) => d._id === selectedDriverId);

    assignTransportDriver(request._id, selectedDriverId, {
      name: driver.name,
      mobile: driver.mobile,
      vehicleNumber: driver.vehicleNumber,
    })
      .then((response) => {
        if (response && response.status === 200) {
          toast.success("Driver assigned successfully");
          this.setState({ showDriverModal: false });
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to assign driver");
        }
      });
  };

  handleCreateAndAssignDriver = () => {
    const { request, newDriver } = this.state;

    if (!newDriver.name || !newDriver.mobile) {
      toast.error("Driver name and mobile are required");
      return;
    }

    // Create new driver first
    createTransportDriver(newDriver)
      .then((response) => {
        if (response && response.status === 201) {
          const createdDriver = response.data;
          // Now assign the driver
          assignTransportDriver(request._id, createdDriver._id, {
            name: createdDriver.name,
            mobile: createdDriver.mobile,
            vehicleNumber: createdDriver.vehicleNumber,
          })
            .then((assignResponse) => {
              if (assignResponse && assignResponse.status === 200) {
                toast.success("Driver created and assigned");
                this.setState({
                  showNewDriverModal: false,
                  showDriverModal: false,
                  newDriver: { name: "", mobile: "", vehicleType: "sedan", vehicleNumber: "" },
                });
                this.loadRequest();
                this.loadDrivers();
              }
            });
        } else {
          toast.error(response?.message || "Failed to create driver");
        }
      });
  };

  handleUpdateStatus = (newStatus) => {
    const { request } = this.state;
    const notes = prompt("Add notes (optional):");

    updateTransportRequestStatus(request._id, newStatus, notes || "")
      .then((response) => {
        if (response && response.status === 200) {
          toast.success(`Status updated to ${newStatus}`);
          this.loadRequest();
        } else {
          toast.error(response?.message || "Failed to update status");
        }
      });
  };

  formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleDateString("en-US", {
      weekday: "short",
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  render() {
    const { request, drivers, showDriverModal, showNewDriverModal, showQuoteModal, selectedDriverId, newDriver, quoteData, isSubmittingQuote } = this.state;

    if (!request) {
      return (
        <div className="page-content">
          <Container fluid>
            <div className="text-center py-5">
              <i className="ri-loader-4-line ri-spin ri-3x"></i>
              <p className="mt-2">Loading...</p>
            </div>
          </Container>
        </div>
      );
    }

    const statusColors = {
      pending: "warning",
      accepted: "info",
      rejected: "danger",
      driver_assigned: "primary",
      in_progress: "info",
      completed: "success",
      cancelled: "secondary",
    };

    // Check if service requires transport/driver (protocol-only doesn't need driver)
    const requiresDriver = ['transport', 'protocol_transport', 'day_rental', 'outstation', 'airport', 'intercity'].includes(request.serviceCategory);
    const isProtocolOnly = request.serviceCategory === 'protocol';

    const statusLabels = {
      pending: "Pending Review",
      accepted: "Accepted",
      rejected: "Rejected",
      driver_assigned: "Driver Assigned",
      in_progress: "In Progress",
      completed: "Completed",
      cancelled: "Cancelled",
    };

    // Service category labels and colors
    const categoryLabels = {
      protocol: "Protocol (Meet & Greet)",
      transport: "Transport (Airport)",
      protocol_transport: "Protocol & Transport",
      day_rental: "Day Rental",
      outstation: "Outstation",
      airport: "Airport Transfer",
      intercity: "Inter-city",
    };

    const categoryColors = {
      protocol: "info",
      transport: "primary",
      protocol_transport: "success",
      day_rental: "warning",
      outstation: "danger",
      airport: "secondary",
      intercity: "secondary",
    };

    const tierLabels = {
      standard: "Standard",
      comfort: "Comfort",
      vip: "VIP",
    };

    const tierColors = {
      standard: "light",
      comfort: "info",
      vip: "warning",
    };

    const tripTypeLabels = {
      one_way: "One-way",
      round_trip: "Round Trip",
      return_same_day: "Return Same Day",
    };

    // Get service category from request
    const serviceCategory = request.serviceCategory || "airport";

    return (
      <React.Fragment>
        <div className="page-content">
          <Container fluid>
            <Breadcrumbs title="Request Details" breadcrumbItems={this.state.breadcrumbItems} />

            {/* Header with status and actions */}
            <Row className="mb-4">
              <Col>
                <Card className="border-0 shadow-sm">
                  <CardBody>
                    <div className="d-flex justify-content-between align-items-center flex-wrap">
                      <div>
                        <h4 className="mb-1">
                          <i className="ri-car-line mr-2 text-primary"></i>
                          {request.requestNumber}
                        </h4>
                        <div className="d-flex flex-wrap align-items-center mt-2">
                          <Badge color={statusColors[request.status]} className="px-3 py-2 mr-2" style={{ fontSize: "12px" }}>
                            {statusLabels[request.status]}
                          </Badge>
                          <Badge color={categoryColors[serviceCategory]} className="px-2 py-1 mr-2" style={{ fontSize: "11px" }}>
                            {categoryLabels[serviceCategory] || serviceCategory}
                          </Badge>
                          {request.serviceType && (
                            <Badge color="soft-dark" className="px-2 py-1 mr-2" style={{ fontSize: "11px" }}>
                              {request.serviceType === "arrival" ? "Arrival" : request.serviceType === "departure" ? "Departure" : "Arrival & Departure"}
                            </Badge>
                          )}
                          {request.tierType && (
                            <Badge color={tierColors[request.tierType]} className="px-2 py-1" style={{ fontSize: "11px" }}>
                              {tierLabels[request.tierType] || request.tierType}
                            </Badge>
                          )}
                        </div>
                      </div>
                      <div className="mt-2 mt-md-0">
                        {/* Quote-based services (day_rental, outstation) with pending_quote status */}
                        {request.status === "pending" && request.quoteStatus === "pending_quote" && (
                          <>
                            <Button color="warning" className="mr-2" onClick={() => this.setState({ showQuoteModal: true })}>
                              <i className="ri-price-tag-3-line mr-1"></i> Set Quote
                            </Button>
                            <Button color="danger" className="mr-2" onClick={this.handleReject}>
                              <i className="ri-close-line mr-1"></i> Reject
                            </Button>
                          </>
                        )}
                        {/* Quote-based services waiting for customer response */}
                        {request.status === "pending" && request.quoteStatus === "quoted" && (
                          <div className="d-flex align-items-center">
                            <Badge color="info" className="px-3 py-2 mr-2">
                              <i className="ri-time-line mr-1"></i> Waiting for Customer Response
                            </Badge>
                            <Button color="warning" size="sm" onClick={() => this.setState({ showQuoteModal: true })}>
                              <i className="ri-edit-line mr-1"></i> Modify Quote
                            </Button>
                          </div>
                        )}
                        {/* Non-quote-based services or accepted quotes */}
                        {request.status === "pending" && (!request.quoteStatus || request.quoteStatus === "not_applicable" || request.quoteStatus === "accepted") && (
                          <>
                            <Button color="success" className="mr-2" onClick={this.handleAccept}>
                              <i className="ri-check-line mr-1"></i> Accept
                            </Button>
                            <Button color="danger" className="mr-2" onClick={this.handleReject}>
                              <i className="ri-close-line mr-1"></i> Reject
                            </Button>
                          </>
                        )}
                        {request.status === "accepted" && requiresDriver && (
                          <Button color="primary" className="mr-2" onClick={() => this.setState({ showDriverModal: true })}>
                            <i className="ri-user-add-line mr-1"></i> Assign Driver
                          </Button>
                        )}
                        {request.status === "accepted" && isProtocolOnly && (
                          <Button color="info" className="mr-2" onClick={() => this.handleUpdateStatus("in_progress")}>
                            <i className="ri-play-line mr-1"></i> Start Service
                          </Button>
                        )}
                        {request.status === "driver_assigned" && (
                          <Button color="secondary" onClick={() => this.setState({ showDriverModal: true })}>
                            <i className="ri-refresh-line mr-1"></i> Change Driver
                          </Button>
                        )}
                        {["pending", "accepted", "driver_assigned"].includes(request.status) && (
                          <Button color="warning" className="ml-2" onClick={this.handleCancel}>
                            <i className="ri-forbid-line mr-1"></i> Cancel
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>

            <Row>
              {/* Left Column - Main Details */}
              <Col lg={8}>
                {/* Services Card */}
                <Card className="mb-4">
                  <CardBody>
                    <h5 className="card-title mb-4">
                      <i className="ri-service-line mr-2"></i>Services & Pricing
                    </h5>
                    <Row>
                      <Col md={8}>
                        {/* New structure display */}
                        {request.serviceCategory && (
                          <div className="mb-3">
                            <div className="service-badge mb-2">
                              <i className={`mr-2 ${
                                serviceCategory === "protocol" ? "ri-user-star-line text-info" :
                                serviceCategory === "transport" ? "ri-car-line text-primary" :
                                serviceCategory === "protocol_transport" ? "ri-vip-crown-line text-success" :
                                serviceCategory === "day_rental" ? "ri-time-line text-warning" :
                                serviceCategory === "outstation" ? "ri-road-map-line text-danger" :
                                "ri-taxi-line text-secondary"
                              }`}></i>
                              <strong>{categoryLabels[serviceCategory] || serviceCategory}</strong>
                            </div>
                            {request.serviceType && (
                              <p className="mb-1 text-muted">
                                <i className={`mr-1 ${request.serviceType === "arrival" ? "ri-flight-land-line" : request.serviceType === "departure" ? "ri-flight-takeoff-line" : "ri-arrow-left-right-line"}`}></i>
                                {request.serviceType === "arrival" ? "Arrival" : request.serviceType === "departure" ? "Departure" : "Arrival & Departure"}
                              </p>
                            )}
                            {request.tierType && (
                              <Badge color={tierColors[request.tierType]} className="mr-2">
                                {tierLabels[request.tierType]} Class
                              </Badge>
                            )}
                            {/* Day Rental specific info */}
                            {serviceCategory === "day_rental" && request.rentalHours && (
                              <Badge color="soft-warning" className="mr-2">
                                {request.rentalHours} Hours
                              </Badge>
                            )}
                            {/* Outstation specific info */}
                            {serviceCategory === "outstation" && request.tripType && (
                              <Badge color="soft-danger" className="mr-2">
                                {tripTypeLabels[request.tripType] || request.tripType}
                              </Badge>
                            )}
                          </div>
                        )}
                        {/* Legacy services display */}
                        {!request.serviceCategory && (
                          <div className="d-flex flex-wrap">
                            {request.services?.pickup && (
                              <div className="service-badge mr-3 mb-2">
                                <i className="ri-flight-land-line text-primary mr-1"></i>
                                <span>Airport Pickup</span>
                                <Badge color="light" className="ml-2">${request.pricing?.pickupPrice || 0}</Badge>
                              </div>
                            )}
                            {request.services?.drop && (
                              <div className="service-badge mr-3 mb-2">
                                <i className="ri-flight-takeoff-line text-info mr-1"></i>
                                <span>Airport Drop</span>
                                <Badge color="light" className="ml-2">${request.pricing?.dropPrice || 0}</Badge>
                              </div>
                            )}
                            {request.services?.meetAndGreet && (
                              <div className="service-badge mr-3 mb-2">
                                <i className="ri-user-smile-line text-success mr-1"></i>
                                <span>Meet & Greet</span>
                                <Badge color={request.pricing?.isMeetGreetFree ? "success" : "light"} className="ml-2">
                                  {request.pricing?.isMeetGreetFree ? "FREE" : `$${request.pricing?.meetAndGreetPrice || 0}`}
                                </Badge>
                              </div>
                            )}
                          </div>
                        )}
                        {/* Vehicle info if assigned */}
                        {request.vehicleTypeId && (
                          <div className="mt-3 pt-3 border-top">
                            <small className="text-muted">Vehicle Type</small>
                            <p className="mb-0 font-weight-medium">
                              <i className="ri-car-line mr-1"></i>
                              {request.vehicleTypeId?.name || request.vehicleTypeName || "N/A"}
                            </p>
                          </div>
                        )}
                      </Col>
                      <Col md={4} className="text-right">
                        <div className="total-price">
                          <small className="text-muted d-block">Total Amount</small>
                          {/* Show quoted price for quote-based services, otherwise show calculated price */}
                          {request.quotedPrice?.amount > 0 ? (
                            <>
                              <h3 className="text-success mb-0">${request.quotedPrice.amount}</h3>
                              <small className="text-muted">{request.pricing?.currency || "USD"}</small>
                              {request.quoteStatus === "quoted" && (
                                <Badge color="warning" className="d-block mt-1">Awaiting Acceptance</Badge>
                              )}
                              {request.quoteStatus === "accepted" && (
                                <Badge color="success" className="d-block mt-1">Quote Accepted</Badge>
                              )}
                            </>
                          ) : (
                            <>
                              <h3 className="text-success mb-0">${request.pricing?.totalPrice || 0}</h3>
                              <small className="text-muted">{request.pricing?.currency || "USD"}</small>
                            </>
                          )}
                        </div>
                      </Col>
                    </Row>

                    {/* Quote Details Section */}
                    {request.quotedPrice?.amount > 0 && (
                      <div className="mt-4 pt-3 border-top">
                        <h6 className="mb-3">
                          <i className="ri-price-tag-3-line mr-2 text-warning"></i>
                          Quote Details
                        </h6>
                        <Row>
                          <Col md={6}>
                            <p className="mb-2">
                              <strong>Quoted Amount:</strong>{" "}
                              <span className="text-success font-weight-bold">${request.quotedPrice.amount}</span>
                            </p>
                            {request.quotedPrice.estimatedKm > 0 && (
                              <p className="mb-2">
                                <strong>Estimated Distance:</strong> {request.quotedPrice.estimatedKm} km
                              </p>
                            )}
                            {request.quotedPrice.perKmRate > 0 && (
                              <p className="mb-2">
                                <strong>Per KM Rate:</strong> ${request.quotedPrice.perKmRate}/km
                              </p>
                            )}
                          </Col>
                          <Col md={6}>
                            {request.quotedPrice.breakdown && (
                              <div>
                                <strong>Breakdown:</strong>
                                <p className="text-muted mb-0 mt-1" style={{ whiteSpace: "pre-wrap" }}>
                                  {request.quotedPrice.breakdown}
                                </p>
                              </div>
                            )}
                            {request.quotedPrice.quotedAt && (
                              <small className="text-muted d-block mt-2">
                                <i className="ri-time-line mr-1"></i>
                                Quoted on {new Date(request.quotedPrice.quotedAt).toLocaleString()}
                              </small>
                            )}
                          </Col>
                        </Row>
                        {request.quoteStatus === "quoted" && (
                          <div className="alert alert-warning mt-3 mb-0">
                            <i className="ri-hourglass-line mr-2"></i>
                            Waiting for customer to accept or reject this quote
                          </div>
                        )}
                        {request.quoteStatus === "rejected" && request.quoteResponse?.rejectionReason && (
                          <div className="alert alert-danger mt-3 mb-0">
                            <i className="ri-close-circle-line mr-2"></i>
                            <strong>Quote Rejected:</strong> {request.quoteResponse.rejectionReason}
                          </div>
                        )}
                      </div>
                    )}
                  </CardBody>
                </Card>

                {/* Pickup Details - For arrival services (Protocol/Transport/Combo) or both */}
                {(request.services?.pickup || (["arrival", "both"].includes(request.serviceType) && request.pickupDetails)) && request.pickupDetails && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-flight-land-line mr-2 text-primary"></i>
                        {serviceCategory === "protocol" ? "Arrival Details" : "Pickup Details"}
                      </h5>
                      <Row>
                        <Col md={6}>
                          <p><strong>Date:</strong> {this.formatDate(request.pickupDetails.date)}</p>
                          <p><strong>Time:</strong> {request.pickupDetails.time || "N/A"}</p>
                          <p><strong>Location:</strong> {request.pickupDetails.location || "Airport"}</p>
                          {request.pickupDetails.dropoffLocation && (
                            <p><strong>Drop-off Location:</strong> {request.pickupDetails.dropoffLocation}</p>
                          )}
                        </Col>
                        <Col md={6}>
                          <p><strong>Flight #:</strong> {request.pickupDetails.flightNumber || "N/A"}</p>
                          <p><strong>Airline:</strong> {request.pickupDetails.airline || "N/A"}</p>
                          <p><strong>Terminal:</strong> {request.pickupDetails.terminal || "N/A"}</p>
                          <p><strong>Arrival Time:</strong> {request.pickupDetails.arrivalTime || "N/A"}</p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Drop Details - For departure services (Protocol/Transport/Combo) or both */}
                {(request.services?.drop || (["departure", "both"].includes(request.serviceType) && request.dropDetails)) && request.dropDetails && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-flight-takeoff-line mr-2 text-info"></i>
                        {serviceCategory === "protocol" ? "Departure Details" : "Drop Details"}
                      </h5>
                      <Row>
                        <Col md={6}>
                          <p><strong>Date:</strong> {this.formatDate(request.dropDetails.date)}</p>
                          <p><strong>Time:</strong> {request.dropDetails.time || "N/A"}</p>
                          <p><strong>Pickup Location:</strong> {request.dropDetails.pickupLocation || request.dropDetails.location || "N/A"}</p>
                          <p><strong>Drop Location:</strong> {request.dropDetails.location || "Airport"}</p>
                        </Col>
                        <Col md={6}>
                          <p><strong>Flight #:</strong> {request.dropDetails.flightNumber || "N/A"}</p>
                          <p><strong>Airline:</strong> {request.dropDetails.airline || "N/A"}</p>
                          <p><strong>Terminal:</strong> {request.dropDetails.terminal || "N/A"}</p>
                          <p><strong>Departure Time:</strong> {request.dropDetails.departureTime || "N/A"}</p>
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Day Rental Details */}
                {serviceCategory === "day_rental" && request.dayRentalDetails && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-time-line mr-2 text-warning"></i>Day Rental Details
                      </h5>
                      <Row>
                        <Col md={6}>
                          <p><strong>Date:</strong> {this.formatDate(request.dayRentalDetails.date)}</p>
                          <p><strong>Start Time:</strong> {request.dayRentalDetails.time || "N/A"}</p>
                          <p><strong>Duration:</strong> {request.dayRentalDetails.duration || "N/A"} hours</p>
                        </Col>
                        <Col md={6}>
                          <p><strong>Pickup Address:</strong> {request.dayRentalDetails.address || "N/A"}</p>
                          {request.vehicleTypePreference && (
                            <p><strong>Vehicle Preference:</strong> {request.vehicleTypePreference}</p>
                          )}
                        </Col>
                      </Row>
                      {/* Map Display - Using OpenStreetMap (free, no API key) */}
                      {request.dayRentalDetails.coordinates?.lat && request.dayRentalDetails.coordinates?.lng && (
                        <div className="mt-3 pt-3 border-top">
                          <h6 className="mb-2">
                            <i className="ri-map-pin-line mr-1 text-primary"></i>
                            Pickup Location
                          </h6>
                          <div className="rounded overflow-hidden" style={{ height: "200px" }}>
                            <iframe
                              width="100%"
                              height="100%"
                              style={{ border: 0 }}
                              loading="lazy"
                              src={`https://www.openstreetmap.org/export/embed.html?bbox=${request.dayRentalDetails.coordinates.lng - 0.01},${request.dayRentalDetails.coordinates.lat - 0.01},${request.dayRentalDetails.coordinates.lng + 0.01},${request.dayRentalDetails.coordinates.lat + 0.01}&layer=mapnik&marker=${request.dayRentalDetails.coordinates.lat},${request.dayRentalDetails.coordinates.lng}`}
                            />
                          </div>
                          <a
                            href={`https://www.google.com/maps?q=${request.dayRentalDetails.coordinates.lat},${request.dayRentalDetails.coordinates.lng}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn btn-outline-primary btn-sm mt-2"
                          >
                            <i className="ri-external-link-line mr-1"></i> Open in Google Maps
                          </a>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}

                {/* Outstation Details */}
                {serviceCategory === "outstation" && request.outstationDetails && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-road-map-line mr-2 text-danger"></i>Outstation Details
                      </h5>
                      <Row>
                        <Col md={6}>
                          <p><strong>Journey Type:</strong> {tripTypeLabels[request.journeyType] || request.journeyType || "N/A"}</p>
                          <p><strong>Date:</strong> {this.formatDate(request.outstationDetails.date)}</p>
                          <p><strong>Time:</strong> {request.outstationDetails.time || "N/A"}</p>
                        </Col>
                        <Col md={6}>
                          {/* For one-way trips */}
                          {request.outstationDetails.pickupLocation && (
                            <p><strong>From:</strong> {request.outstationDetails.pickupLocation}</p>
                          )}
                          {request.outstationDetails.dropLocation && (
                            <p><strong>To:</strong> {request.outstationDetails.dropLocation}</p>
                          )}
                          {/* For return same day trips */}
                          {request.outstationDetails.address && (
                            <p><strong>Start/End Address:</strong> {request.outstationDetails.address}</p>
                          )}
                          {request.outstationDetails.visitingPlace && (
                            <p><strong>Visiting Place:</strong> {request.outstationDetails.visitingPlace}</p>
                          )}
                          {request.vehicleTypePreference && (
                            <p><strong>Vehicle Preference:</strong> {request.vehicleTypePreference}</p>
                          )}
                        </Col>
                      </Row>
                      {/* Map Display for Outstation Locations */}
                      {((request.outstationDetails.pickupCoordinates?.lat && request.outstationDetails.pickupCoordinates?.lng) ||
                        (request.outstationDetails.dropCoordinates?.lat && request.outstationDetails.dropCoordinates?.lng) ||
                        (request.outstationDetails.addressCoordinates?.lat && request.outstationDetails.addressCoordinates?.lng) ||
                        (request.outstationDetails.visitingPlaceCoordinates?.lat && request.outstationDetails.visitingPlaceCoordinates?.lng)) && (
                        <div className="mt-3 pt-3 border-top">
                          <h6 className="mb-3">
                            <i className="ri-map-pin-line mr-1 text-primary"></i>
                            Locations on Map
                          </h6>
                          <Row>
                            {/* Pickup / Start Address Map */}
                            {(request.outstationDetails.pickupCoordinates?.lat || request.outstationDetails.addressCoordinates?.lat) && (
                              <Col md={6} className="mb-3">
                                <small className="text-muted d-block mb-1">
                                  {request.outstationDetails.pickupCoordinates?.lat ? "Pickup Location" : "Start/End Address"}
                                </small>
                                <div className="rounded overflow-hidden" style={{ height: "150px" }}>
                                  {(() => {
                                    const lat = request.outstationDetails.pickupCoordinates?.lat || request.outstationDetails.addressCoordinates?.lat;
                                    const lng = request.outstationDetails.pickupCoordinates?.lng || request.outstationDetails.addressCoordinates?.lng;
                                    return (
                                      <iframe
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                                      />
                                    );
                                  })()}
                                </div>
                                <a
                                  href={`https://www.google.com/maps?q=${request.outstationDetails.pickupCoordinates?.lat || request.outstationDetails.addressCoordinates?.lat},${request.outstationDetails.pickupCoordinates?.lng || request.outstationDetails.addressCoordinates?.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-primary btn-sm mt-1 btn-block"
                                >
                                  <i className="ri-external-link-line mr-1"></i> Open in Maps
                                </a>
                              </Col>
                            )}
                            {/* Drop / Visiting Place Map */}
                            {(request.outstationDetails.dropCoordinates?.lat || request.outstationDetails.visitingPlaceCoordinates?.lat) && (
                              <Col md={6} className="mb-3">
                                <small className="text-muted d-block mb-1">
                                  {request.outstationDetails.dropCoordinates?.lat ? "Drop Location" : "Visiting Place"}
                                </small>
                                <div className="rounded overflow-hidden" style={{ height: "150px" }}>
                                  {(() => {
                                    const lat = request.outstationDetails.dropCoordinates?.lat || request.outstationDetails.visitingPlaceCoordinates?.lat;
                                    const lng = request.outstationDetails.dropCoordinates?.lng || request.outstationDetails.visitingPlaceCoordinates?.lng;
                                    return (
                                      <iframe
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        loading="lazy"
                                        src={`https://www.openstreetmap.org/export/embed.html?bbox=${lng - 0.01},${lat - 0.01},${lng + 0.01},${lat + 0.01}&layer=mapnik&marker=${lat},${lng}`}
                                      />
                                    );
                                  })()}
                                </div>
                                <a
                                  href={`https://www.google.com/maps?q=${request.outstationDetails.dropCoordinates?.lat || request.outstationDetails.visitingPlaceCoordinates?.lat},${request.outstationDetails.dropCoordinates?.lng || request.outstationDetails.visitingPlaceCoordinates?.lng}`}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-outline-primary btn-sm mt-1 btn-block"
                                >
                                  <i className="ri-external-link-line mr-1"></i> Open in Maps
                                </a>
                              </Col>
                            )}
                          </Row>
                        </div>
                      )}
                    </CardBody>
                  </Card>
                )}

                {/* Protocol/Visa Details */}
                {(serviceCategory === "protocol" || serviceCategory === "protocol_transport") && request.visaType && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-passport-line mr-2 text-info"></i>Protocol Details
                      </h5>
                      <Row>
                        <Col md={6}>
                          <p><strong>Visa Type:</strong> {request.visaType || "N/A"}</p>
                          {request.nationality && (
                            <p><strong>Nationality:</strong> {request.nationality}</p>
                          )}
                        </Col>
                        <Col md={6}>
                          {request.protocolNotes && (
                            <p><strong>Protocol Notes:</strong> {request.protocolNotes}</p>
                          )}
                        </Col>
                      </Row>
                    </CardBody>
                  </Card>
                )}

                {/* Special Requests */}
                {request.specialRequests && (
                  <Card className="mb-4">
                    <CardBody>
                      <h5 className="card-title mb-3">
                        <i className="ri-message-2-line mr-2"></i>Special Requests
                      </h5>
                      <p className="mb-0 text-muted">{request.specialRequests}</p>
                    </CardBody>
                  </Card>
                )}
              </Col>

              {/* Right Column - Guest & Driver Info */}
              <Col lg={4}>
                {/* Guest Info */}
                <Card className="mb-4">
                  <CardBody>
                    <h5 className="card-title mb-4">
                      <i className="ri-user-3-line mr-2"></i>Guest Information
                    </h5>
                    <div className="guest-info">
                      <p className="mb-2">
                        <i className="ri-user-line mr-2 text-muted"></i>
                        <strong>{request.contactName || request.userId?.name || "N/A"}</strong>
                      </p>
                      <p className="mb-2">
                        <i className="ri-phone-line mr-2 text-muted"></i>
                        {request.contactMobile || request.userId?.mobileNo || "N/A"}
                      </p>
                      <p className="mb-2">
                        <i className="ri-mail-line mr-2 text-muted"></i>
                        {request.contactEmail || request.userId?.email || "N/A"}
                      </p>
                      <hr />
                      <p className="mb-2">
                        <i className="ri-group-line mr-2 text-muted"></i>
                        <strong>{request.passengers || 1}</strong> Passenger(s)
                      </p>
                      <p className="mb-2">
                        <i className="ri-briefcase-line mr-2 text-muted"></i>
                        <strong>{request.luggage || 0}</strong> Luggage
                      </p>
                    </div>
                  </CardBody>
                </Card>

                {/* Driver Info */}
                {request.assignedDriver && (
                  <Card className="mb-4 border-primary">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-car-line mr-2 text-primary"></i>Assigned Driver
                      </h5>
                      <div className="driver-info">
                        <p className="mb-2">
                          <strong>{request.assignedDriver.name}</strong>
                        </p>
                        <p className="mb-2">
                          <i className="ri-phone-line mr-2 text-muted"></i>
                          {request.assignedDriver.mobile}
                        </p>
                        {request.assignedDriver.vehicleNumber && (
                          <p className="mb-2">
                            <i className="ri-car-line mr-2 text-muted"></i>
                            {request.assignedDriver.vehicleNumber}
                          </p>
                        )}
                        <small className="text-muted">
                          Assigned on {this.formatDate(request.driverAssignedAt)}
                        </small>
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Payment Info */}
                <Card className="mb-4">
                  <CardBody>
                    <h5 className="card-title mb-4">
                      <i className="ri-money-dollar-circle-line mr-2"></i>Payment Information
                    </h5>
                    <div className="payment-info">
                      <p className="mb-2">
                        <i className="ri-wallet-line mr-2 text-muted"></i>
                        <strong>Method:</strong> {request.paymentMethod === "cash" ? "Cash" : request.paymentMethod === "card" ? "Card" : request.paymentMethod === "room_charge" ? "Room Charge" : request.paymentMethod === "online" ? "Online" : request.paymentMethod || "Not specified"}
                      </p>
                      <p className="mb-3">
                        <i className="ri-checkbox-circle-line mr-2 text-muted"></i>
                        <strong>Status:</strong>{" "}
                        <Badge
                          color={
                            request.paymentStatus === "paid" ? "success" :
                            request.paymentStatus === "refunded" ? "info" :
                            request.paymentStatus === "waived" ? "secondary" :
                            "warning"
                          }
                          className="ml-1"
                        >
                          {request.paymentStatus === "paid" ? "Paid" :
                           request.paymentStatus === "refunded" ? "Refunded" :
                           request.paymentStatus === "waived" ? "Waived" :
                           "Pending"}
                        </Badge>
                      </p>
                      {/* Show Mark Payment Received button for cash payments that are pending */}
                      {request.paymentMethod === "cash" &&
                       request.paymentStatus !== "paid" &&
                       ["driver_assigned", "in_progress", "completed"].includes(request.status) && (
                        <Button
                          color="success"
                          size="sm"
                          block
                          onClick={this.handleMarkPaymentReceived}
                          disabled={this.state.isUpdatingPayment}
                        >
                          {this.state.isUpdatingPayment ? (
                            <>
                              <i className="ri-loader-4-line ri-spin mr-1"></i> Updating...
                            </>
                          ) : (
                            <>
                              <i className="ri-checkbox-circle-line mr-1"></i> Mark Payment Received
                            </>
                          )}
                        </Button>
                      )}
                      {request.paymentStatus === "paid" && (
                        <small className="text-success d-block mt-2">
                          <i className="ri-check-double-line mr-1"></i>
                          Payment confirmed
                        </small>
                      )}
                    </div>
                  </CardBody>
                </Card>

                {/* Status History */}
                {request.statusHistory && request.statusHistory.length > 0 && (
                  <Card>
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-history-line mr-2"></i>Status History
                      </h5>
                      <div className="status-timeline">
                        {request.statusHistory.map((history, idx) => (
                          <div key={idx} className="timeline-item mb-3">
                            <Badge color={statusColors[history.status] || "secondary"} className="mr-2">
                              {statusLabels[history.status] || history.status}
                            </Badge>
                            <small className="text-muted d-block mt-1">
                              {new Date(history.changedAt).toLocaleString()}
                            </small>
                            {history.notes && (
                              <small className="text-muted d-block">{history.notes}</small>
                            )}
                          </div>
                        ))}
                      </div>
                    </CardBody>
                  </Card>
                )}

                {/* Feedback */}
                {request.feedback && request.feedback.rating && (
                  <Card className="mt-4">
                    <CardBody>
                      <h5 className="card-title mb-4">
                        <i className="ri-star-line mr-2 text-warning"></i>Guest Feedback
                      </h5>
                      <div className="feedback-info">
                        <div className="mb-2">
                          <strong>Overall Rating:</strong>
                          <span className="ml-2">
                            {[...Array(5)].map((_, i) => (
                              <i
                                key={i}
                                className={`ri-star-${i < request.feedback.rating ? "fill" : "line"} text-warning`}
                              ></i>
                            ))}
                          </span>
                        </div>
                        {request.feedback.driverRating && (
                          <div className="mb-2">
                            <strong>Driver Rating:</strong>
                            <span className="ml-2">
                              {[...Array(5)].map((_, i) => (
                                <i
                                  key={i}
                                  className={`ri-star-${i < request.feedback.driverRating ? "fill" : "line"} text-warning`}
                                ></i>
                              ))}
                            </span>
                          </div>
                        )}
                        {request.feedback.comment && (
                          <p className="text-muted mb-0 mt-2">"{request.feedback.comment}"</p>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                )}
              </Col>
            </Row>
          </Container>
        </div>

        {/* Assign Driver Modal */}
        <Modal isOpen={showDriverModal} toggle={() => this.setState({ showDriverModal: false })}>
          <ModalHeader toggle={() => this.setState({ showDriverModal: false })}>
            Assign Driver
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Select Existing Driver</Label>
              <Input
                type="select"
                value={selectedDriverId}
                onChange={(e) => this.setState({ selectedDriverId: e.target.value })}
              >
                <option value="">-- Select Driver --</option>
                {drivers.map((driver) => (
                  <option key={driver._id} value={driver._id}>
                    {driver.name} - {driver.mobile} {driver.vehicleNumber ? `(${driver.vehicleNumber})` : ""}
                  </option>
                ))}
              </Input>
            </FormGroup>
            <div className="text-center my-3">
              <span className="text-muted">or</span>
            </div>
            <Button color="outline-primary" block onClick={() => this.setState({ showNewDriverModal: true, showDriverModal: false })}>
              <i className="ri-add-line mr-1"></i> Create New Driver
            </Button>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ showDriverModal: false })}>
              Cancel
            </Button>
            <Button color="primary" onClick={this.handleAssignDriver} disabled={!selectedDriverId}>
              Assign Driver
            </Button>
          </ModalFooter>
        </Modal>

        {/* New Driver Modal */}
        <Modal isOpen={showNewDriverModal} toggle={() => this.setState({ showNewDriverModal: false })}>
          <ModalHeader toggle={() => this.setState({ showNewDriverModal: false })}>
            Create & Assign New Driver
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Driver Name *</Label>
              <Input
                type="text"
                value={newDriver.name}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, name: e.target.value } })}
                placeholder="Enter driver name"
              />
            </FormGroup>
            <FormGroup>
              <Label>Mobile Number *</Label>
              <Input
                type="text"
                value={newDriver.mobile}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, mobile: e.target.value } })}
                placeholder="Enter mobile number"
              />
            </FormGroup>
            <FormGroup>
              <Label>Vehicle Type</Label>
              <Input
                type="select"
                value={newDriver.vehicleType}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, vehicleType: e.target.value } })}
              >
                <option value="sedan">Sedan</option>
                <option value="suv">SUV</option>
                <option value="van">Van</option>
                <option value="bus">Bus</option>
                <option value="other">Other</option>
              </Input>
            </FormGroup>
            <FormGroup>
              <Label>Vehicle Number</Label>
              <Input
                type="text"
                value={newDriver.vehicleNumber}
                onChange={(e) => this.setState({ newDriver: { ...newDriver, vehicleNumber: e.target.value } })}
                placeholder="Enter vehicle number (optional)"
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ showNewDriverModal: false, showDriverModal: true })}>
              Back
            </Button>
            <Button color="primary" onClick={this.handleCreateAndAssignDriver}>
              Create & Assign
            </Button>
          </ModalFooter>
        </Modal>

        {/* Set Quote Modal (for day_rental and outstation services) */}
        <Modal isOpen={showQuoteModal} toggle={() => this.setState({ showQuoteModal: false })}>
          <ModalHeader toggle={() => this.setState({ showQuoteModal: false })}>
            <i className="ri-price-tag-3-line mr-2"></i>
            Set Price Quote
          </ModalHeader>
          <ModalBody>
            {/* Show customer's vehicle preference if available */}
            {request?.vehicleTypePreference && request.vehicleTypePreference !== "any" && (
              <div className="alert alert-info mb-3">
                <i className="ri-car-line mr-2"></i>
                <strong>Customer Preference:</strong> {request.vehicleTypePreference.charAt(0).toUpperCase() + request.vehicleTypePreference.slice(1)}
              </div>
            )}
            {/* Show service details for reference */}
            {request && (
              <div className="border p-3 rounded mb-3" style={{ backgroundColor: "var(--bs-tertiary-bg, #f8f9fa)" }}>
                <small className="text-muted d-block">Service Details</small>
                <p className="mb-1"><strong>Service:</strong> {request.serviceCategory === "day_rental" ? "Day Rental" : "Outstation"}</p>
                {request.dayRentalDetails && (
                  <>
                    <p className="mb-1"><strong>Duration:</strong> {request.dayRentalDetails.duration || "N/A"} hours</p>
                    <p className="mb-0"><strong>Pickup:</strong> {request.dayRentalDetails.address || "N/A"}</p>
                  </>
                )}
                {request.outstationDetails && (
                  <>
                    <p className="mb-1"><strong>Journey:</strong> {request.outstationDetails.pickupLocation || request.outstationDetails.address || "N/A"} → {request.outstationDetails.dropLocation || request.outstationDetails.visitingPlace || "N/A"}</p>
                    <p className="mb-1"><strong>Journey Type:</strong> {request.journeyType || "N/A"}</p>
                    {request.tripDetails?.estimatedKm && (
                      <p className="mb-0"><strong>Est. Distance:</strong> {request.tripDetails.estimatedKm} km</p>
                    )}
                  </>
                )}
              </div>
            )}
            <FormGroup>
              <Label>Quote Amount (USD) *</Label>
              <Input
                type="number"
                value={quoteData.amount}
                onChange={(e) => this.setState({ quoteData: { ...quoteData, amount: e.target.value } })}
                placeholder="Enter total quote amount"
                min="0"
                step="0.01"
              />
            </FormGroup>
            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label>Per KM Rate (Optional)</Label>
                  <Input
                    type="number"
                    value={quoteData.perKmRate}
                    onChange={(e) => this.setState({ quoteData: { ...quoteData, perKmRate: e.target.value } })}
                    placeholder="Rate per km"
                    min="0"
                    step="0.01"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label>Estimated KM (Optional)</Label>
                  <Input
                    type="number"
                    value={quoteData.estimatedKm}
                    onChange={(e) => this.setState({ quoteData: { ...quoteData, estimatedKm: e.target.value } })}
                    placeholder="Estimated kilometers"
                    min="0"
                  />
                </FormGroup>
              </Col>
            </Row>
            <FormGroup>
              <Label>Quote Breakdown (Optional)</Label>
              <Input
                type="textarea"
                value={quoteData.breakdown}
                onChange={(e) => this.setState({ quoteData: { ...quoteData, breakdown: e.target.value } })}
                placeholder="E.g., Base fare: $50, Fuel: $30, Driver allowance: $20"
                rows="3"
              />
              <small className="text-muted">This will be shown to the customer</small>
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => this.setState({ showQuoteModal: false })}>
              Cancel
            </Button>
            <Button color="warning" onClick={this.handleSetQuote} disabled={isSubmittingQuote || !quoteData.amount}>
              {isSubmittingQuote ? (
                <>
                  <i className="ri-loader-4-line ri-spin mr-1"></i> Sending...
                </>
              ) : (
                <>
                  <i className="ri-send-plane-line mr-1"></i> Send Quote to Customer
                </>
              )}
            </Button>
          </ModalFooter>
        </Modal>

        {/* Accept Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showAcceptModal}
          toggle={this.toggleAcceptModal}
          onConfirm={this.confirmAccept}
          title="Accept Transport Request"
          message="Are you sure you want to accept this transport request?"
          confirmText="Accept"
          confirmColor="success"
          icon="ri-checkbox-circle-line"
          loading={this.state.actionLoading}
        />

        {/* Payment Confirmation Modal */}
        <ConfirmModal
          isOpen={this.state.showPaymentConfirmModal}
          toggle={this.togglePaymentConfirmModal}
          onConfirm={this.confirmMarkPaymentReceived}
          title="Confirm Payment"
          message="Mark payment as received for this booking?"
          confirmText="Confirm Payment"
          confirmColor="success"
          icon="ri-money-dollar-circle-line"
          loading={this.state.isUpdatingPayment}
        />

        {/* Reason Input Modal (for Reject/Cancel) */}
        <Modal isOpen={this.state.showReasonModal} toggle={this.toggleReasonModal} centered>
          <ModalHeader toggle={this.toggleReasonModal}>
            {this.state.reasonModalType === "reject" ? "Reject Request" : "Cancel Request"}
          </ModalHeader>
          <ModalBody>
            <FormGroup>
              <Label>Please enter a reason:</Label>
              <Input
                type="textarea"
                rows="3"
                value={this.state.reasonText}
                onChange={(e) => this.setState({ reasonText: e.target.value })}
                placeholder={this.state.reasonModalType === "reject"
                  ? "Enter rejection reason..."
                  : "Enter cancellation reason..."}
              />
            </FormGroup>
          </ModalBody>
          <ModalFooter>
            <Button color="light" onClick={this.toggleReasonModal} disabled={this.state.actionLoading}>
              Cancel
            </Button>
            <Button
              color={this.state.reasonModalType === "reject" ? "warning" : "danger"}
              onClick={this.confirmReasonAction}
              disabled={this.state.actionLoading}
            >
              {this.state.actionLoading ? (
                <><i className="bx bx-loader bx-spin mr-1"></i> Processing...</>
              ) : (
                this.state.reasonModalType === "reject" ? "Reject" : "Cancel Request"
              )}
            </Button>
          </ModalFooter>
        </Modal>

        <ToastContainer />
      </React.Fragment>
    );
  }
}

export default TransportRequestDetail;
